import {useCallback} from 'react';
import * as THREE from 'three';
import {
    Batch,
    OperationGroup,
    Dimension,
    Edge,
    PreviewLocation,
    PreviewFormValues,
    detectLShapeVariant,
} from 'components/customer/Preview3D/types';
import {
    createPanelGeometry,
    getPantryDuplicateBoxTopZ,
    isPantryDuplicateBoxTop,
    loadCachedTexture,
    loadDataXML,
} from 'components/customer/Preview3D/helpers';
import {getMaterial} from 'components/customer/Materials/store/selectors/materialSelector';
import {useAppSelector, useAppDispatch} from 'store/customer';
import {shallowEqual} from 'react-redux';
import {MaterialType} from 'components/customer/Materials/entity';
import {getEdge} from 'components/customer/Materials/store/selectors/edgeSelector';
import useMaterialTexture from 'components/customer/Preview3D/lib/useMaterialTexture';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {setIsRightHangProduct} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {use3DContext} from 'components/customer/Preview3D/components/Preview3DComponent';
import {
    detectShakerDoor,
    hasSplitDoors,
    buildsShakerPerOperationGroup,
    isDoorOperationGroup,
    isShakerFoldKitDoor,
    isSplitDoorOperationGroup,
} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';
import {applyShakerDoorMaterialsDefault} from 'components/customer/Preview3DCommon/3d-models/shaker';
import addShakerDoorWithAnimation from 'components/customer/Preview3DCommon/hardware/CreateShakerDoor';
import {getRightLShapeBifoldLeftDoorXOffset} from 'components/customer/Preview3DCommon/values/door';
import {getPanelThickness} from 'components/customer/Preview3DCommon/shapes/advancedShelf';
import {isValidPositionOperationGroup} from 'components/customer/RoomPlanner/helpers/scene';
import {applyOperationGroupTransform} from 'components/customer/Preview3DCommon/helpers/applyOperationGroupTransform';
import {getPartAttributes} from 'components/customer/RoomPlanner/helpers/getProductAttributes';
import {
    BevelFaceFlags,
    createBeveledPanelGeometry,
    isBeveledFramePanel,
} from 'components/customer/Preview3DCommon/hardware/useBeveledFrame';
import {isFloatingVanityTemplate} from 'components/customer/RoomPlanner/helpers/renderer/panelTypeHelpers';

const getEdgeValue = (
    panelValue: string | undefined,
    cabinetValue: string | undefined
): boolean => {
    if (typeof panelValue !== 'undefined') {
        return Boolean(parseInt(panelValue));
    }
    if (typeof cabinetValue !== 'undefined') {
        return Boolean(parseInt(cabinetValue));
    }
    return false;
};

// Advanced doors sit in the scene at y = -cabinetDepth / 2, outside the
// cabinet group. A return product's blind panel protrudes in front of the
// carcase, so centring on the full bbox would push the carcase away from them.
// The carcase sits RETURN_DOOR_CARCASE_GAP behind the doors.
export const RETURN_DOOR_CARCASE_GAP = 2;

export const getCabinetGroupCenterY = (
    box: THREE.Box3,
    isReturnProduct: boolean,
    cabinetDepth: number
): number => {
    if (isReturnProduct && cabinetDepth > 0) {
        return (
            -(box.max.y - cabinetDepth) -
            cabinetDepth / 2 +
            RETURN_DOOR_CARCASE_GAP
        );
    }
    return -box.min.y - (box.max.y - box.min.y) / 2;
};

const usePreview3D = () => {
    const dispatch = useAppDispatch();
    const selectedExteriorMaterial = useAppSelector(
        (state) => getMaterial(state, MaterialType.EXTERIOR),
        shallowEqual
    );
    const selectedCarcaseMaterial = useAppSelector(
        (state) => getMaterial(state, MaterialType.CARCASE),
        shallowEqual
    );
    const selectedExteriorMaterialEdge = useAppSelector((state) =>
        getEdge(state, MaterialType.EXTERIOR)
    );
    const selectedCarcaseMaterialEdge = useAppSelector((state) =>
        getEdge(state, MaterialType.CARCASE)
    );

    const {values, productDataStore} = use3DContext();
    const {
        exteriorThickness,
        carcaseThickness,
        carcaseSubstrateMaterial,
        exteriorSubstrateMaterial,
    } = useMaterialTexture();

    const horizontalGrainExt = values.hor_grain_ext;
    const horizontalGrainCarc = values.hor_grain_carc;
    const includeHardware = values.cabinet_include_hardware;

    const variables: TemplateVariable = JSON.parse(
        productDataStore.current?.template_3d[0].attributes?.variables
    ) as TemplateVariable;

    const isFloatingVanity = isFloatingVanityTemplate(
        productDataStore.current?.template_3d?.[0]?.attributes?.template
    );

    const edging: Edge | null =
        typeof values.cabinet_edge_L1 !== 'undefined' ||
        typeof values.panel_edge_top !== 'undefined'
            ? {
                  l1: getEdgeValue(
                      values.panel_edge_left as string,
                      values.cabinet_edge_L1 as string
                  ),
                  l2: getEdgeValue(
                      values.panel_edge_right as string,
                      values.cabinet_edge_L2 as string
                  ),
                  w1: getEdgeValue(
                      values.panel_edge_top as string,
                      values.cabinet_edge_W1 as string
                  ),
                  w2: getEdgeValue(
                      values.panel_edge_bottom as string,
                      values.cabinet_edge_W2 as string
                  ),
              }
            : null;

    // 13 - Panel Adjustable Hole
    if (values.cabinet_type == 13) {
        const prevEdging = {...edging};
        edging.l1 = prevEdging.l2;
        edging.l2 = prevEdging.l1;
        edging.w1 = prevEdging.w2;
        edging.w2 = prevEdging.w1;
    }

    // 87 - Manual Panel Exterior
    // 40 - Manual Panel Interior
    // 389 - Finger Pullout
    if ([87, 40, 389].includes(values.cabinet_type)) {
        const prevEdging = {...edging};
        edging.l1 = prevEdging.w1;
        edging.l2 = prevEdging.w2;
        edging.w1 = prevEdging.l1;
        edging.w2 = prevEdging.l2;
    }

    // 85 - Kick Length
    if (
        values.cabinet_type == 85 ||
        values.cabinet_type == 229 ||
        values.cabinet_type == 230 ||
        (values as unknown as {type_name?: string}).type_name === 'KickLength'
    ) {
        const prevEdging = {...edging};
        edging.l1 = prevEdging.w1;
        edging.l2 = prevEdging.w2;
        edging.w1 = prevEdging.l1;
        edging.w2 = prevEdging.l2;
    }

    const isDrawerFront = useCallback((operationGroup: OperationGroup) => {
        return operationGroup.IsDrawerFront || operationGroup.IsDoorPullout;
    }, []);

    const createSceneXML = useCallback(
        (
            batch: Batch,
            animationHandler: (
                mesh: THREE.Group<THREE.Object3DEventMap>,
                operationGroup: OperationGroup,
                meshPos: Dimension,
                counter: number
            ) => void,
            manager: THREE.LoadingManager,
            showTexture?: boolean,
            includeDrawerFaces?: boolean,
            partiallyTransparentSegment?: boolean,
            showShelfLabel?: boolean,
            previewLocation?: PreviewLocation,
            values?: PreviewFormValues
        ) => {
            if (typeof batch === 'undefined' || batch == null) return;

            if (showTexture) {
                [
                    selectedExteriorMaterial?.image,
                    selectedCarcaseMaterial?.image,
                    selectedExteriorMaterialEdge?.image,
                    selectedCarcaseMaterialEdge?.image,
                ]
                    .filter(Boolean)
                    .forEach((url) => void loadCachedTexture(url));
            }

            const cabinetGroup = new THREE.Group();
            let counter = 0;

            // Detect kicker frame / bulkhead products so they get beveled panels.
            const _tpl =
                productDataStore.current?.template_3d?.[0]?.attributes
                    ?.template ?? '';
            const isKickerOrBulkhead =
                Boolean(
                    (variables as unknown as {isBulkheadComponent?: boolean})
                        ?.isBulkheadComponent
                ) ||
                _tpl.includes('Bulkhead') ||
                _tpl.includes('KickerFramed');

            // 2-part pantry duplicate box-top height (one thickness under partition).
            const pantryBoxTopZ = getPantryDuplicateBoxTopZ(
                variables?.shelfType === 'PANTRY' && !variables?.is2Part,
                values?.cabinet_partition_height,
                carcaseThickness
            );

            for (const job of Object.values(batch.Jobs)) {
                for (const manufacturing of Object.values(job.Manufacturing)) {
                    const operationGroups = Object.values(
                        manufacturing.OperationGroups
                    );

                    if (
                        operationGroups.find((g) => g.IsRightDoor) &&
                        !operationGroups.find((g) => g.IsLeftDoor)
                    )
                        dispatch(setIsRightHangProduct(true));

                    const isPairDoor = operationGroups.some(
                        (g) => g.IsPairDoor || g.IsLeftBifoldExtension
                    );
                    const isBiFoldDoor = operationGroups.some(
                        (g) =>
                            g.IsBifoldLeftDoor ||
                            g.IsLeftBifoldExtension ||
                            g.IsLeftBifoldExtension
                    );
                    const isRightDoor = operationGroups.some(
                        (g) => g.IsRightDoor
                    );
                    const isRightLShape = operationGroups.some(
                        (g) => g.IsRightLShape
                    );

                    // CO-4836 scoping: the new shared door/leg transform
                    // pipeline only applies to L-shape cabinets. Everything
                    // else keeps master's original inline transform so we
                    // don't regress base/standard cabinets (feet + door drift).
                    const isLShapeCabinet = Boolean(
                        detectLShapeVariant(
                            productDataStore.current?.template_3d?.[0]
                                ?.attributes?.template,
                            variables
                        )
                    );
                    const isTopHang = operationGroups.some(
                        (g) => g.IsTopHangDoor
                    );
                    const isSplitDoor = hasSplitDoors(operationGroups);

                    for (const operationGroup of operationGroups) {
                        if (
                            isLShapeCabinet &&
                            !isValidPositionOperationGroup(operationGroup, true)
                        ) {
                            continue;
                        }

                        // Skip pantry duplicate box-top; keep the labelled partition.
                        if (
                            isPantryDuplicateBoxTop(
                                operationGroup,
                                pantryBoxTopZ
                            )
                        ) {
                            continue;
                        }

                        const part = Object.values(job.Items)[0].Parts[
                            operationGroup.PartID
                        ];

                        const shakerDoorInfo = detectShakerDoor(
                            selectedExteriorMaterial
                        );

                        const partName = part.Description?.split(' ');
                        const searchWords = 'Base Right Side'.split(' ');

                        const isRightSideSubstringPresent = searchWords.every(
                            (word) => partName.includes(word)
                        );

                        const transparentRightSidePanel =
                            isRightSideSubstringPresent &&
                            previewLocation === 'SHELVES';

                        let isDoor = isDoorOperationGroup(operationGroup);

                        let nonSupplyCarcase =
                            operationGroup.IsDrawerRunner &&
                            !Boolean(values.cabinet_include_hardware);

                        if (
                            (variables.innerDrawerCount &&
                                !variables.isBinWithInnerDrawerProduct) ||
                            values?.isInnerDrawer
                        ) {
                            if (operationGroup.IsDrawerFront) {
                                isDoor = false;
                                nonSupplyCarcase = !includeHardware;
                            }
                        }

                        if (
                            !includeDrawerFaces &&
                            operationGroup.ExteriorMaterial
                        ) {
                            continue;
                        }

                        if (isDrawerFront(operationGroup)) counter++;

                        let mesh;

                        if (
                            shakerDoorInfo.isShakerDoor &&
                            isDoor &&
                            buildsShakerPerOperationGroup(operationGroup, {
                                isBiFoldDoor,
                                isTopHang,
                                isSplitDoor,
                            })
                        ) {
                            // Check if we should use shaker doors instead
                            const applyShakerDoorMaterials = (
                                model: THREE.Group,
                                showTexture: boolean
                            ) => {
                                applyShakerDoorMaterialsDefault(
                                    model,
                                    showTexture,
                                    selectedExteriorMaterial?.image
                                );
                            };
                            mesh = addShakerDoorWithAnimation({
                                operationGroup,
                                variables,
                                values,
                                exteriorThickness,
                                carcaseThickness,
                                applyShakerDoorMaterials,
                                showTexture,
                                isPairDoor,
                                isRightDoor,
                                isBiFoldDoor,
                                isRightLShape,
                                isSplitDoor:
                                    isSplitDoorOperationGroup(operationGroup),
                            });
                            if (variables.hasUpperLiftSystem) {
                                const box = new THREE.Box3().setFromObject(
                                    mesh
                                );
                                const size = new THREE.Vector3();
                                box.getSize(size);

                                const height = size.y;

                                mesh.position.z =
                                    values.cabinet_height / 2 - height / 2;
                                mesh.position.z -= values?.cabinet_door_top / 2;
                                mesh.position.z +=
                                    values?.cabinet_door_bottom / 2;
                            }
                        } else if (isDoor && shakerDoorInfo?.isShakerDoor) {
                            continue;
                        } else if (
                            isKickerOrBulkhead &&
                            isBeveledFramePanel(operationGroup)
                        ) {
                            // Kicker frame / bulkhead panels get 45-degree
                            // beveled edges so adjacent panels join seamlessly.
                            const isExtMat =
                                Boolean(operationGroup.ExteriorMaterial) ||
                                Boolean(operationGroup.CarcaseMaterial)
                                    ? operationGroup.ExteriorMaterial?.toString() ===
                                      'true'
                                    : false;
                            const bevelMaterialImage = isExtMat
                                ? (selectedExteriorMaterial?.image ?? null)
                                : (selectedCarcaseMaterial?.image ?? null);
                            mesh = createBeveledPanelGeometry(
                                manager,
                                operationGroup,
                                '#000',
                                showTexture,
                                bevelMaterialImage,
                                true,
                                isExtMat,
                                {exteriorThickness, carcaseThickness},
                                false,
                                operationGroups?.length ?? 0,
                                {
                                    hasBackFace: Boolean(
                                        Number(values?.cabinet_edge_L1)
                                    ),
                                    hasLeftFace: Boolean(
                                        Number(values?.cabinet_edge_W1)
                                    ),
                                    hasRightFace: Boolean(
                                        Number(values?.cabinet_edge_W2)
                                    ),
                                    hasFrontFace: Boolean(
                                        Number(values?.cabinet_edge_L2)
                                    ),
                                } satisfies BevelFaceFlags,
                                Boolean(values?.hor_grain_ext),
                                Boolean(values?.hor_grain_carc)
                            );
                        } else {
                            const {panelThickness, centerOffset} =
                                getPanelThickness(
                                    values,
                                    Boolean(operationGroup?.ShelfLabel),
                                    carcaseThickness
                                );

                            mesh = createPanelGeometry(
                                manager,
                                operationGroup,
                                '#000',
                                showTexture,
                                selectedExteriorMaterial?.image,
                                selectedCarcaseMaterial?.image,
                                selectedExteriorMaterialEdge?.image,
                                selectedCarcaseMaterialEdge?.image,
                                true,
                                Boolean(operationGroup.ExteriorMaterial) ||
                                    Boolean(operationGroup.CarcaseMaterial)
                                    ? operationGroup.ExteriorMaterial?.toString() ===
                                          'true'
                                    : null,
                                Boolean(horizontalGrainExt),
                                Boolean(horizontalGrainCarc),
                                Boolean(operationGroup.NoEdge)
                                    ? {
                                          l1: false,
                                          l2: false,
                                          w1: false,
                                          w2: false,
                                      }
                                    : edging,
                                nonSupplyCarcase,
                                operationGroup?.IsDrawerRunner ||
                                    (operationGroup?.IsDrawerFront &&
                                        Boolean(variables?.innerDrawerCount) &&
                                        Boolean(
                                            operationGroup?.IsBinWithInnerDrawer
                                        )),
                                {
                                    exteriorThickness,
                                    carcaseThickness: panelThickness,
                                    centerOffset,
                                },
                                Boolean(operationGroup.HideSgement) ||
                                    transparentRightSidePanel,
                                partiallyTransparentSegment ||
                                    transparentRightSidePanel,
                                operationGroup?.ShelfLabel,
                                showShelfLabel,
                                carcaseSubstrateMaterial,
                                exteriorSubstrateMaterial,
                                includeHardware,
                                variables?.isLeftReturn,
                                variables?.isReturnProduct &&
                                    !variables?.isLeftReturn,
                                operationGroups?.length,
                                variables?.isUpperProduct ||
                                    variables?.isApplianceProduct ||
                                    isFloatingVanity,
                                Boolean(operationGroup.NoEdging),
                                operationGroup.IsRightDoor ||
                                    operationGroup.IsPairDoor ||
                                    operationGroup.IsLeftDoor ||
                                    operationGroup.IsCorFoldKitLeftDoor ||
                                    operationGroup.IsCorFoldKitRightDoor ||
                                    operationGroup.IsDoorPullout ||
                                    operationGroup.IsTopHangDoor ||
                                    operationGroup.IsBifoldLeftDoor ||
                                    operationGroup.IsBifoldRightDoor ||
                                    operationGroup.IsCornerLeftDoor ||
                                    operationGroup.IsCornerRightDoor ||
                                    operationGroup.IsDrawerFront ||
                                    operationGroup.WithEdging ||
                                    operationGroup.IsLeftApplianceMainDoor ||
                                    operationGroup.IsLeftApplianceExtendedDoor ||
                                    operationGroup.IsRightApplianceMainDoor ||
                                    operationGroup.IsRightApplianceExtendedDoor ||
                                    (operationGroup.ExteriorMaterial &&
                                        variables?.isReturnProduct),
                                Boolean(operationGroup.FlipSubstrate),
                                Boolean(operationGroup.IsFrontRangehoodVent),
                                Boolean(operationGroup.WithTopEdging),
                                Boolean(operationGroup.WithFrontEdging),
                                Boolean(operationGroup.DynamicExteriorEdging),
                                false,
                                Boolean(operationGroup.WithBackEdging)
                            );

                            if (
                                isLShapeCabinet &&
                                operationGroup.IsBifoldLeftDoor
                            ) {
                                mesh.position.x +=
                                    getRightLShapeBifoldLeftDoorXOffset(
                                        Boolean(operationGroup.IsRightLShape),
                                        Boolean(
                                            operationGroup.IsLeftBifoldExtension
                                        ),
                                        exteriorThickness
                                    );
                            }
                        }

                        if (mesh) {
                            if (isLShapeCabinet) {
                                // L-shape: canonical door-positioning pipeline
                                // shared with RoomPlanner (scene.ts) so the two
                                // stay 1:1.
                                const meshPos = applyOperationGroupTransform({
                                    mesh,
                                    operationGroup,
                                    variables,
                                    values,
                                    thickness: {
                                        exteriorThickness,
                                        carcaseThickness,
                                    },
                                    shakerDoorInfo,
                                    templateName:
                                        productDataStore.current?.template_3d[0]
                                            .attributes.template ?? '',
                                });

                                animationHandler(
                                    mesh,
                                    operationGroup,
                                    meshPos,
                                    counter
                                );

                                cabinetGroup.add(mesh);
                            } else {
                                // Non-L-shape: master's original inline
                                // transform, restored verbatim (pre-CO-4836)
                                // so standard cabinets are untouched.
                                const meshPos =
                                    operationGroup.Operations[0].Insert;
                                const meshRot =
                                    operationGroup.Operations[0].Rotation;
                                const meshNormal =
                                    operationGroup.Operations[0].Segments[0]
                                        .StartNormal;

                                const matrixRot = new THREE.Matrix4();
                                matrixRot.makeRotationAxis(
                                    new THREE.Vector3(
                                        -meshNormal.x,
                                        -meshNormal.y,
                                        -meshNormal.z
                                    ),
                                    THREE.MathUtils.degToRad(meshRot + 90)
                                );

                                const matrix = new THREE.Matrix4();
                                const up =
                                    meshNormal.z == 0
                                        ? new THREE.Vector3(0, 0, 1)
                                        : new THREE.Vector3(0, 1, 0);
                                if (
                                    operationGroup.MfgOrientationID !==
                                        undefined &&
                                    meshNormal.z != 0
                                ) {
                                    up.negate();
                                }

                                const {isDrawer} = getPartAttributes(
                                    operationGroup,
                                    variables,
                                    values
                                );

                                const animatesAfterTransform =
                                    isDrawer ||
                                    isShakerFoldKitDoor(mesh, operationGroup);

                                if (!animatesAfterTransform) {
                                    animationHandler(
                                        mesh,
                                        operationGroup,
                                        meshPos,
                                        counter
                                    );
                                }

                                matrix.lookAt(
                                    new THREE.Vector3(0, 0, 0),
                                    new THREE.Vector3(
                                        meshNormal.x,
                                        meshNormal.y,
                                        meshNormal.z
                                    ),
                                    up
                                );
                                matrixRot.multiply(matrix);
                                matrixRot.setPosition(
                                    new THREE.Vector3(
                                        meshPos.x,
                                        meshPos.y,
                                        meshPos.z
                                    )
                                );

                                mesh.applyMatrix4(matrixRot);

                                if (operationGroup.IsBifoldLeftDoor) {
                                    mesh.rotation.y = (90 * Math.PI) / 180;
                                }
                                if (
                                    operationGroup.IsCornerLeftDoor ||
                                    operationGroup.IsCornerRightDoor
                                ) {
                                    mesh.rotation.y = (45 * Math.PI) / 180;
                                }
                                if (operationGroup.FlipY) {
                                    mesh.rotation.y = (180 * Math.PI) / 180;
                                }
                                if (operationGroup.RotationY) {
                                    mesh.rotation.y =
                                        (Number(operationGroup.RotationY) *
                                            Math.PI) /
                                        180;
                                }

                                if (animatesAfterTransform) {
                                    animationHandler(
                                        mesh,
                                        operationGroup,
                                        meshPos,
                                        counter
                                    );
                                }

                                cabinetGroup.add(mesh);
                            }
                        }
                    }
                }
            }

            const box = new THREE.Box3();
            cabinetGroup.children.forEach((child) => {
                if (child.name === 'ShakerDoors') return;
                box.expandByObject(child);
            });
            if (box.isEmpty()) box.setFromObject(cabinetGroup);
            const cabinetSize = box.getSize(new THREE.Vector3());
            const cabinetZOffset = variables.hasUpperLiftSystem
                ? previewLocation === 'DOORS'
                    ? cabinetSize.z * 0.75
                    : cabinetSize.z
                : cabinetSize.z / 2;

            cabinetGroup.position.set(
                -box.min.x - cabinetSize.x / 2,
                getCabinetGroupCenterY(
                    box,
                    Boolean(variables.isReturnProduct),
                    Number(values?.cabinet_depth ?? 0)
                ),
                -box.min.z - cabinetZOffset
            );

            return cabinetGroup;
        },
        [
            selectedExteriorMaterial,
            selectedCarcaseMaterial,
            horizontalGrainExt,
            horizontalGrainCarc,
            selectedExteriorMaterialEdge,
            selectedCarcaseMaterialEdge,
            edging,
            includeHardware,
            carcaseSubstrateMaterial,
            exteriorSubstrateMaterial,
            exteriorThickness,
            carcaseThickness,
            dispatch,
            isDrawerFront,
        ]
    );

    return {loadDataXML, createSceneXML};
};

export default usePreview3D;
